import * as React from 'react';
import { type OdaLimitDurumu, type Plan } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';
import { Alan, Secim } from '@/components/ui/field';
import { ApiHatasi, api } from '@/lib/api';

/**
 * Oda yonetimi + plan limiti.
 *
 * Onceden odalar yalnizca tesis kurulumunda olusuyordu; sonradan oda eklemek
 * ya da numarasini degistirmek icin veritabanina girmek gerekiyordu.
 *
 * Limit YOLUN BASINDA gorunur (sayac + cubuk), sonunda degil: otel duvara
 * carpmadan once nerede oldugunu bilmeli. Duvara carpinca da ceza degil,
 * yol gosteren bir yukseltme penceresi acilir.
 */

interface Oda {
  id: string;
  no: string;
  kat: string | null;
  blok: string | null;
  durum: 'BOS' | 'DOLU' | 'ARIZALI' | 'BLOKE';
  temizlikDurumu: string;
  aktif: boolean;
  roomTypeId: string;
  odaTipiAd: string;
  odaTipiKod: string;
}

interface OdaTipi {
  id: string;
  ad: string;
  kod: string;
}

interface PlanOzellik {
  odaLimiti: number;
  ocrGunlukKota: number;
  aciklama: string;
}

const PLAN_ADI: Record<Plan, string> = {
  DENEME: 'Deneme',
  BASLANGIC: 'Başlangıç',
  PROFESYONEL: 'Profesyonel',
  KURUMSAL: 'Kurumsal',
};

export function Odalar({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('room:write');

  const [odalar, setOdalar] = React.useState<Oda[]>([]);
  const [tipler, setTipler] = React.useState<OdaTipi[]>([]);
  const [limit, setLimit] = React.useState<OdaLimitDurumu | null>(null);
  const [planlar, setPlanlar] = React.useState<Record<Plan, PlanOzellik> | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [duzenlenen, setDuzenlenen] = React.useState<Oda | 'yeni' | null>(null);
  const [paywall, setPaywall] = React.useState<OdaLimitDurumu | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      const v = await api<{
        odalar: Oda[];
        tipler: OdaTipi[];
        limit: OdaLimitDurumu;
        planlar: Record<Plan, PlanOzellik>;
      }>('/api/odalar');
      setOdalar(v.odalar);
      setTipler(v.tipler);
      setLimit(v.limit);
      setPlanlar(v.planlar);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Odalar alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  /** Limit dolu ise paywall'i acar; degilse formu. */
  function odaEkle() {
    if (limit?.doldu) setPaywall(limit);
    else setDuzenlenen('yeni');
  }

  async function aktifDegistir(oda: Oda) {
    if (!yazabilir || islenen) return;
    setIslenen(oda.id);
    setHata(null);
    try {
      await api(`/api/odalar/${oda.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ aktif: !oda.aktif }),
      });
      await yukle();
    } catch (e) {
      // Limit dolu iken pasif odayi geri acmak da paywall'i tetikler.
      if (e instanceof ApiHatasi && e.status === 402) setPaywall(e.detay as OdaLimitDurumu);
      else setHata(e instanceof ApiHatasi ? e.message : 'Değiştirilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  async function sil(oda: Oda) {
    if (!yazabilir || islenen) return;
    setIslenen(oda.id);
    setHata(null);
    try {
      await api(`/api/odalar/${oda.id}`, { method: 'DELETE' });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Silinemedi.');
    } finally {
      setIslenen(null);
    }
  }

  const aktifler = odalar.filter((o) => o.aktif);
  const pasifler = odalar.filter((o) => !o.aktif);
  const doluluk = limit ? Math.min(100, Math.round((limit.kullanilan / Math.max(1, limit.limit)) * 100)) : 0;
  // %80'den sonra uyari rengine gecer: otel duvara carpmadan once gorsun.
  const cubukRengi = !limit ? '#1f4fd8' : limit.doldu ? '#e5484d' : doluluk >= 80 ? '#f2b632' : '#1f4fd8';

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Odalar</h1>
        {limit && (
          <Rozet renk={limit.doldu ? 'hata' : doluluk >= 80 ? 'uyari' : 'notr'}>
            {limit.kullanilan} / {limit.limit} oda
          </Rozet>
        )}
        {yazabilir && (
          <Button className="ml-auto" onClick={odaEkle}>
            + Oda ekle
          </Button>
        )}
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      {limit && planlar && (
        <Kart className="[animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both]">
          <KartGovde className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-lacivert">
                {PLAN_ADI[limit.plan]} paketi · {planlar[limit.plan].aciklama}
              </span>
              <span className="text-[13px] text-gri">
                {limit.doldu ? (
                  <strong className="text-kirmizi">Limit doldu</strong>
                ) : (
                  <>
                    <strong className="text-lacivert">{limit.kalan}</strong> oda daha ekleyebilirsiniz
                  </>
                )}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-satir">
              <div
                className="h-full transition-[width] duration-500"
                style={{ width: `${doluluk}%`, background: cubukRengi }}
              />
            </div>
            {limit.doldu && limit.sonrakiPlan && (
              <p className="text-[13px] leading-relaxed text-gri">
                Yeni oda eklemek için <strong className="text-lacivert">{PLAN_ADI[limit.sonrakiPlan]}</strong>{' '}
                paketine geçmeniz gerekiyor ({limit.sonrakiLimit} odaya kadar).{' '}
                <button onClick={() => setPaywall(limit)} className="font-semibold text-marka-600 underline">
                  Ayrıntı
                </button>
              </p>
            )}
          </KartGovde>
        </Kart>
      )}

      {yukleniyor ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-[18px] border border-cizgi bg-white" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-3 [animation:rise_.6s_.1s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {aktifler.map((oda) => (
              <OdaKarti
                key={oda.id}
                oda={oda}
                yazabilir={yazabilir}
                islenen={islenen === oda.id}
                onDuzenle={() => setDuzenlenen(oda)}
                onAktif={() => void aktifDegistir(oda)}
                onSil={() => void sil(oda)}
              />
            ))}
          </div>

          {pasifler.length > 0 && (
            <div className="flex flex-col gap-3">
              <h2 className="text-[15px] font-bold text-gri">
                Kullanımdan kaldırılan odalar ({pasifler.length})
              </h2>
              <p className="text-[13px] leading-relaxed text-gri">
                Bu odalar satışa kapalıdır ve plan limitine sayılmaz. Geçmiş rezervasyonları ve folyoları
                yerinde durur; istediğinizde geri açabilirsiniz.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pasifler.map((oda) => (
                  <OdaKarti
                    key={oda.id}
                    oda={oda}
                    yazabilir={yazabilir}
                    islenen={islenen === oda.id}
                    onDuzenle={() => setDuzenlenen(oda)}
                    onAktif={() => void aktifDegistir(oda)}
                    onSil={() => void sil(oda)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {duzenlenen && (
        <OdaFormu
          oda={duzenlenen === 'yeni' ? null : duzenlenen}
          tipler={tipler}
          onKapat={() => setDuzenlenen(null)}
          onKaydedildi={async () => {
            setDuzenlenen(null);
            await yukle();
          }}
          onLimitDoldu={(l) => {
            setDuzenlenen(null);
            setPaywall(l);
          }}
        />
      )}

      {paywall && planlar && (
        <Paywall limit={paywall} planlar={planlar} onKapat={() => setPaywall(null)} />
      )}
    </main>
  );
}

function OdaKarti({
  oda,
  yazabilir,
  islenen,
  onDuzenle,
  onAktif,
  onSil,
}: {
  oda: Oda;
  yazabilir: boolean;
  islenen: boolean;
  onDuzenle: () => void;
  onAktif: () => void;
  onSil: () => void;
}) {
  return (
    <div
      className={
        'flex flex-col gap-2.5 rounded-[18px] border border-cizgi bg-white p-4 ' + (oda.aktif ? '' : 'opacity-60')
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-[22px] font-bold leading-none tracking-[-.02em]">{oda.no}</span>
          <span className="mt-1 truncate text-xs text-gri">{oda.odaTipiAd}</span>
        </div>
        {oda.durum === 'DOLU' && <Rozet renk="bilgi">Dolu</Rozet>}
        {oda.durum === 'ARIZALI' && <Rozet renk="uyari">Arızalı</Rozet>}
        {!oda.aktif && <Rozet renk="notr">Kapalı</Rozet>}
      </div>

      {(oda.kat || oda.blok) && (
        <span className="text-xs text-gri">
          {[oda.blok && `${oda.blok} blok`, oda.kat && `${oda.kat}. kat`].filter(Boolean).join(' · ')}
        </span>
      )}

      {yazabilir && (
        <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
          <button
            onClick={onDuzenle}
            disabled={islenen}
            className="rounded-full border-[1.5px] border-cizgi px-3 py-1.5 text-xs font-semibold text-lacivert transition hover:border-lacivert disabled:opacity-60"
          >
            Düzenle
          </button>
          <button
            onClick={onAktif}
            disabled={islenen}
            className="rounded-full px-3 py-1.5 text-xs font-semibold text-gri transition hover:text-lacivert disabled:opacity-60"
          >
            {oda.aktif ? 'Kullanımdan kaldır' : 'Geri aç'}
          </button>
          {/* Kalici silme yalnizca hic kullanilmamis odada calisir; sunucu reddeder. */}
          {!oda.aktif && (
            <button
              onClick={onSil}
              disabled={islenen}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-gri transition hover:text-red-600 disabled:opacity-60"
            >
              Sil
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function OdaFormu({
  oda,
  tipler,
  onKapat,
  onKaydedildi,
  onLimitDoldu,
}: {
  oda: Oda | null;
  tipler: OdaTipi[];
  onKapat: () => void;
  onKaydedildi: () => void | Promise<void>;
  onLimitDoldu: (l: OdaLimitDurumu) => void;
}) {
  const [no, setNo] = React.useState(oda?.no ?? '');
  const [roomTypeId, setRoomTypeId] = React.useState(oda?.roomTypeId ?? tipler[0]?.id ?? '');
  const [kat, setKat] = React.useState(oda?.kat ?? '');
  const [blok, setBlok] = React.useState(oda?.blok ?? '');
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (gonderiliyor || !no.trim() || !roomTypeId) return;
    setGonderiliyor(true);
    setHata(null);
    const govde = { no: no.trim(), roomTypeId, kat: kat.trim() || null, blok: blok.trim() || null };
    try {
      if (oda) await api(`/api/odalar/${oda.id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      else await api('/api/odalar', { method: 'POST', body: JSON.stringify(govde) });
      await onKaydedildi();
    } catch (err) {
      if (err instanceof ApiHatasi && err.status === 402) {
        onLimitDoldu(err.detay as OdaLimitDurumu);
        return;
      }
      setHata(err instanceof ApiHatasi ? err.message : 'Kaydedilemedi.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4" onClick={onKapat} role="presentation">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={kaydet}
        className="flex w-full max-w-[440px] flex-col gap-4 rounded-[20px] bg-white p-6 [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <h2 className="text-xl font-bold">{oda ? `Oda ${oda.no}` : 'Yeni oda'}</h2>

        <Alan
          etiket="Oda numarası"
          value={no}
          onChange={(e) => setNo(e.target.value)}
          maxLength={10}
          autoFocus
          ipucu="Oda planında ve misafirin QR adresinde bu numara görünür."
        />
        <Secim
          etiket="Oda tipi"
          value={roomTypeId}
          onChange={(e) => setRoomTypeId(e.target.value)}
          secenekler={tipler.map((t) => ({ deger: t.id, metin: `${t.ad} (${t.kod})` }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Alan etiket="Kat" value={kat} onChange={(e) => setKat(e.target.value)} maxLength={10} />
          <Alan etiket="Blok" value={blok} onChange={(e) => setBlok(e.target.value)} maxLength={10} />
        </div>

        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" varyant="sessiz" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button type="submit" yukleniyor={gonderiliyor} disabled={!no.trim() || !roomTypeId}>
            {oda ? 'Kaydet' : 'Oda ekle'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Paket yukseltme penceresi (paywall).
 *
 * Bu bir B2B araci ve kendi kendine satin alma YOK: sahte bir "Satin al"
 * dugmesi koymak yaniltici olurdu. Dugme bir TALEP gonderir, talep platforma
 * e-posta olarak duser ve plani platform paneli degistirir.
 *
 * Icerik de dogrudan: neyin engellendigi, bir ust paketin ne getirdigi ve
 * mevcut verinin ne olacagi. Aciklamalar tesisin kendi rakamlariyla yazilir.
 */
function Paywall({
  limit,
  planlar,
  onKapat,
}: {
  limit: OdaLimitDurumu;
  planlar: Record<Plan, PlanOzellik>;
  onKapat: () => void;
}) {
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [gonderildi, setGonderildi] = React.useState(false);
  const [not, setNot] = React.useState('');
  const [hata, setHata] = React.useState<string | null>(null);

  const hedef = limit.sonrakiPlan;

  async function talepGonder() {
    if (!hedef || gonderiliyor) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      await api('/api/odalar/paket-talebi', {
        method: 'POST',
        body: JSON.stringify({ hedefPlan: hedef, not: not.trim() || undefined }),
      });
      setGonderildi(true);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Talep gönderilemedi.');
    } finally {
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/70 p-4" onClick={onKapat} role="presentation">
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        className="flex max-h-[90dvh] w-full max-w-[520px] flex-col gap-5 overflow-y-auto rounded-[22px] bg-white p-6 [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both] sm:p-7"
      >
        {gonderildi ? (
          <>
            <span className="grid size-12 place-items-center rounded-full bg-yesil-zemin text-yesil">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-bold">Talebiniz alındı</h2>
              <p className="text-sm leading-relaxed text-gri">
                {hedef && PLAN_ADI[hedef]} paketine geçiş talebiniz iletildi. Ekibimiz iletişime geçip planı
                yükseltecek; yükseltme yapılır yapılmaz yeni odaları ekleyebilirsiniz. Bu sırada tesisiniz
                normal çalışmaya devam eder.
              </p>
            </div>
            <Button onClick={onKapat}>Kapat</Button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-bold uppercase tracking-[.1em] text-marka-600">
                Oda limiti doldu
              </span>
              <h2 className="text-2xl font-bold tracking-[-.02em]">
                {PLAN_ADI[limit.plan]} paketi {limit.limit} odaya kadar
              </h2>
              <p className="text-sm leading-relaxed text-gri">
                Şu an <strong className="text-lacivert">{limit.kullanilan} aktif odanız</strong> var ve paketin
                sınırına ulaştınız. Yeni oda eklemek için bir üst pakete geçmeniz gerekiyor.
              </p>
            </div>

            {hedef ? (
              <>
                <div className="flex flex-col gap-3 rounded-[18px] border-[1.5px] border-marka-600 bg-mavi-zemin p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-lg font-bold text-lacivert">{PLAN_ADI[hedef]}</span>
                    <span className="text-sm font-semibold text-marka-600">{planlar[hedef].aciklama}</span>
                  </div>
                  <div className="flex flex-col gap-2 text-[13.5px] text-lacivert">
                    <span className="flex items-center gap-2">
                      <Tik />
                      <span>
                        <strong>{planlar[hedef].odaLimiti} odaya</strong> kadar (şu an {limit.limit})
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Tik />
                      <span>
                        Günlük <strong>{planlar[hedef].ocrGunlukKota} kimlik okuma</strong> (şu an{' '}
                        {planlar[limit.plan].ocrGunlukKota})
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Tik />
                      <span>Tüm ekranlar ve özellikler aynı kalır</span>
                    </span>
                  </div>
                </div>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-slate-700">Eklemek istediğiniz not (isteğe bağlı)</span>
                  <textarea
                    value={not}
                    onChange={(e) => setNot(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Örn. sezon başında 12 oda daha açıyoruz"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-marka-500 focus:outline-none focus:ring-2 focus:ring-marka-100"
                  />
                </label>

                {hata && <p className="text-sm text-red-600">{hata}</p>}

                <div className="flex flex-col gap-2">
                  <Button onClick={() => void talepGonder()} yukleniyor={gonderiliyor} boyut="lg">
                    {PLAN_ADI[hedef]} paketine geçmek istiyorum
                  </Button>
                  {/*
                    Sahte bir "Satin al" koymuyoruz: odeme akisi yok, plani
                    platform paneli degistiriyor. Dugmenin ne yaptigini
                    onceden soylemek, tiklayip sasirmaktan iyidir.
                  */}
                  <span className="text-center text-xs leading-relaxed text-[#8a93ad]">
                    Talebiniz ekibimize iletilir, ödeme burada alınmaz. Mevcut odalarınız ve verileriniz
                    olduğu gibi kalır.
                  </span>
                </div>
              </>
            ) : (
              <div className="rounded-[18px] bg-zemin p-4 text-sm leading-relaxed text-gri">
                En üst pakettesiniz. Daha fazla oda için bizimle iletişime geçin; tesisinize özel bir limit
                tanımlayabiliriz.
              </div>
            )}

            <button onClick={onKapat} className="text-sm font-semibold text-gri hover:text-lacivert">
              Şimdilik kapat
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Tik() {
  return (
    <span className="grid size-[18px] flex-none place-items-center rounded-full bg-marka-600 text-white">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2.5 6l2.5 2.5 4.5-5" />
      </svg>
    </span>
  );
}
